/** Public auditor-verification page — Phase 2A-aud-pub.
 *
 * Unauthenticated route at `/verify/:sessionId?t=:token`. An EXTERNAL
 * auditor pastes or follows a shared link; we call the public
 * verify endpoint + render pass/fail with the chain head hash + per-
 * TSA anchor cross-references.
 *
 * Deliberately minimal chrome — no TopBar, no nav — because the
 * auditor doesn't have an account with us. The page is a single
 * verdict box + supporting evidence rows.
 */
import { useQuery } from "@tanstack/react-query";
import { formatDateTime } from "@/lib/format";
import axios from "axios";
import { useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";


type TokenResult = {
  backend: string;
  state: string;
  ok: boolean;
  anchored_at: string | null;
  block_height: number | null;
  anchor_url: string;
  serial: string;
  blockchain: string;
};

type VerifyResponse = {
  ok: boolean;
  session_id: string;
  issuing_org_name: string;
  event_count: number;
  chain_head_hash: string | null;
  chain_integrity: { ok: boolean; broken_at_sequence: number | null };
  tokens: TokenResult[];
  audit_chain_started_at: string | null;
  link: { token_prefix: string; created_at: string; expires_at: string };
};


export default function PublicVerifyPage() {
  const { t } = useTranslation();
  const { sessionId } = useParams<{ sessionId: string }>();
  const [params] = useSearchParams();
  const token = params.get("t") ?? "";

  // Build a bare axios instance — we don't want to inherit the JWT
  // Bearer header the regular `api` instance attaches, since this is
  // an unauthenticated public endpoint.
  const bare = useMemo(() => axios.create(), []);

  const verify = useQuery({
    queryKey: ["public-verify", sessionId, token],
    queryFn: async () => {
      const res = await bare.get<VerifyResponse>(
        `/api/v1/audit/public-verify/${sessionId}`,
        { params: { t: token } },
      );
      return res.data;
    },
    enabled: !!sessionId && !!token,
    retry: false,
  });

  if (!token) {
    return (
      <Shell>
        <Status tone="fail" headline={t("ui.publicVerify.missingTokenHeadline")}>
          <Trans
            i18nKey="ui.publicVerify.missingTokenBody"
            values={{ path: `/verify/${sessionId ?? "&lt;session-id&gt;"}?t=cvp_av_…` }}
          >
            The URL must include a <code>?t=</code> query parameter. The
            operator who shared this link should have sent a URL of the
            form{" "}
            <code>{"{{path}}"}</code>.
          </Trans>
        </Status>
      </Shell>
    );
  }

  if (verify.isLoading) {
    return (
      <Shell>
        <p className="text-sm text-nb-text-3">{t("ui.publicVerify.loading")}</p>
      </Shell>
    );
  }

  if (verify.isError) {
    const err = verify.error as { response?: { status?: number } };
    const status = err?.response?.status;
    return (
      <Shell>
        <Status
          tone="fail"
          headline={
            status === 404
              ? t("ui.publicVerify.errorExpiredHeadline")
              : t("ui.publicVerify.errorGenericHeadline")
          }
        >
          {status === 404
            ? t("ui.publicVerify.error404Body")
            : t("ui.publicVerify.errorGenericBody")}
        </Status>
      </Shell>
    );
  }

  const data = verify.data!;
  return (
    <Shell>
      <Status
        tone={data.ok ? "pass" : "fail"}
        headline={
          data.ok
            ? t("ui.publicVerify.passHeadline")
            : t("ui.publicVerify.failHeadline")
        }
      >
        {data.ok
          ? t("ui.publicVerify.passBody")
          : data.chain_integrity.ok
            ? t("ui.publicVerify.tokenFailBody")
            : t("ui.publicVerify.brokenBody", {
                sequence: data.chain_integrity.broken_at_sequence,
              })}
      </Status>

      <Card title={t("ui.publicVerify.sessionCardTitle")}>
        <Row label={t("ui.publicVerify.rowSessionId")}>
          <code className="font-mono text-xs">{data.session_id}</code>
        </Row>
        <Row label={t("ui.publicVerify.rowIssuingOrg")}>{data.issuing_org_name}</Row>
        {data.audit_chain_started_at && (
          <Row label={t("ui.publicVerify.rowChainStarted")}>
            {formatDateTime(data.audit_chain_started_at)}
          </Row>
        )}
        <Row label={t("ui.publicVerify.rowEventCount")}>{data.event_count}</Row>
        {data.chain_head_hash && (
          <Row label={t("ui.publicVerify.rowChainHeadHash")}>
            <code className="break-all font-mono text-[11px] text-nb-text">
              {data.chain_head_hash}
            </code>
          </Row>
        )}
      </Card>

      <Card title={t("ui.publicVerify.anchorsCardTitle")}>
        {data.tokens.length === 0 ? (
          <p className="text-xs text-nb-text-3">
            {t("ui.publicVerify.noAnchors")}
          </p>
        ) : (
          <ul className="space-y-2 text-xs">
            {data.tokens.map((tok, i) => (
              <li
                key={i}
                className={`rounded-md border px-3 py-2 ${
                  tok.ok && tok.state === "anchored"
                    ? "border-emerald-700 bg-emerald-900/20"
                    : tok.state === "anchored"
                      ? "border-cvp-danger bg-cvp-danger/10"
                      : "border-nb-border bg-slate-900/40"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{prettyBackend(tok.backend)}</span>
                  <span
                    className={`rounded px-2 py-0.5 ${
                      tok.ok && tok.state === "anchored"
                        ? "bg-emerald-500/15 text-emerald-300"
                        : tok.state === "anchored"
                          ? "bg-cvp-danger/20 text-cvp-danger"
                          : "bg-slate-700 text-nb-text"
                    }`}
                  >
                    {tok.state === "anchored"
                      ? tok.ok
                        ? t("ui.publicVerify.stateVerified")
                        : t("ui.publicVerify.stateVerificationFailed")
                      : tok.state}
                  </span>
                </div>
                {tok.anchored_at && (
                  <p className="mt-1 text-nb-text-3">
                    {t("ui.publicVerify.anchoredAt", {
                      when: formatDateTime(tok.anchored_at),
                    })}
                  </p>
                )}
                {tok.block_height != null && (
                  <p className="mt-1 text-nb-text">
                    {tok.blockchain
                      ? t("ui.publicVerify.blockHeight", {
                          blockchain: tok.blockchain,
                          height: tok.block_height,
                        })
                      : t("ui.publicVerify.blockDepth", {
                          height: tok.block_height,
                        })}
                  </p>
                )}
                {tok.serial && (
                  <p className="mt-1 text-nb-text">
                    {t("ui.publicVerify.tsaSerial")}{" "}
                    <code className="font-mono">{tok.serial}</code>
                  </p>
                )}
                {tok.anchor_url && (
                  <p className="mt-1 text-nb-text">
                    {t("ui.publicVerify.crossReferenceAt")}{" "}
                    <a
                      className="text-nb-mint hover:underline"
                      href={tok.anchor_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {tok.anchor_url}
                    </a>
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title={t("ui.publicVerify.aboutCardTitle")}>
        <p className="text-xs text-nb-text-2">
          {t("ui.publicVerify.aboutBody")}
        </p>
        <p className="mt-2 text-xs text-nb-text-3">
          {t("ui.publicVerify.linkIssued", {
            created: formatDateTime(data.link.created_at),
            expires: formatDateTime(data.link.expires_at),
          })}
        </p>
      </Card>
    </Shell>
  );
}


function Shell({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="min-h-dvh bg-nb-bg">
      <main className="mx-auto max-w-3xl space-y-4 px-6 py-12">
        <header className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight text-nb-text">
            {t("ui.publicVerify.appTitle")}
          </h1>
          <p className="text-xs text-nb-text-3">
            {t("ui.publicVerify.appSubtitle")}
          </p>
        </header>
        {children}
      </main>
    </div>
  );
}


function Status({
  tone,
  headline,
  children,
}: {
  tone: "pass" | "fail";
  headline: string;
  children: React.ReactNode;
}) {
  return (
    <section
      className={`rounded-lg border-2 px-4 py-3 ${
        tone === "pass"
          ? "border-emerald-500 bg-emerald-900/20"
          : "border-cvp-danger bg-cvp-danger/10"
      }`}
      role="status"
      data-testid="verify-status"
      data-tone={tone}
    >
      <h2
        className={`text-base font-semibold ${
          tone === "pass" ? "text-emerald-300" : "text-cvp-danger"
        }`}
      >
        {tone === "pass" ? "✓ " : "✗ "}
        {headline}
      </h2>
      <p className="mt-1 text-sm text-nb-text">{children}</p>
    </section>
  );
}


function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-nb-border bg-slate-900/40 px-4 py-3">
      <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-nb-text-2">
        {title}
      </h2>
      {children}
    </section>
  );
}


function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-2 py-1 text-xs">
      <div className="text-nb-text-3">{label}</div>
      <div className="col-span-2 text-nb-text">{children}</div>
    </div>
  );
}


function prettyBackend(slug: string): string {
  switch (slug) {
    case "tezos_yodaledger":
      return "Tezos blockchain";
    case "rfc3161_freetsa":
      return "RFC 3161 — FreeTSA";
    case "rfc3161_datasure":
      return "RFC 3161 — DataSure (QTSP)";
    case "rfc3161_generic":
      return "RFC 3161";
    case "opentimestamps":
      return "OpenTimestamps (Bitcoin)";
    default:
      return slug;
  }
}
